import type {
  CreateTaskArgs,
  CreateTaskResponse,
  GetTaskReviewsArgs,
  ProcessTaskReviewArgs,
  SubmitTaskProofArgs,
  TaskActionResponse,
  TaskReportRow,
  TaskReviewRow,
  UserTaskRow,
} from '../types/tasks';
import { apiUrl, domain, parseJsonOrThrow } from './http';

interface TaskListResponse<T> extends TaskActionResponse {
  data?: T[] | null;
}

/** Parse the JSON body, throwing on `status: 'error'`. */
const parseTaskResponse = async <T extends TaskActionResponse>(
  res: Response,
  fallbackError: string
): Promise<T> => {
  const result = await parseJsonOrThrow<T>(res);
  if (result.status === 'error') {
    throw new Error(result.message ?? fallbackError);
  }
  return result;
};

/** Build multipart body, skipping `undefined` fields. */
const toFormData = (
  fields: Record<string, string | number | Blob | undefined>
) => {
  const data = new FormData();
  for (const [key, value] of Object.entries(fields)) {
    if (value === undefined) continue;
    data.append(key, value instanceof Blob ? value : String(value));
  }
  return data;
};

/** Absolute URL for a review's `proof_file` (the API returns it relative to its domain). */
export const taskProofUrl = (path: string): string =>
  /^https?:\/\//.test(path) ? path : `${domain}/${path.replace(/^\/+/, '')}`;

/** GET — every task with paid coins, completion count and per-user details. */
export const getTaskReport = async (): Promise<TaskReportRow[]> => {
  const res = await fetch(apiUrl('/php/get_task_report.php'));
  const result = await parseTaskResponse<TaskListResponse<TaskReportRow>>(
    res,
    'Не вдалося завантажити звіт по завданнях'
  );
  return result.data ?? [];
};

/** GET — submitted proofs filtered by status (backend default: `pending`, 20 rows). */
export const getTaskReviews = async ({
  status,
  limit,
}: GetTaskReviewsArgs = {}): Promise<TaskReviewRow[]> => {
  const params = new URLSearchParams();
  if (status != null) params.set('status', status);
  if (limit != null) params.set('limit', String(limit));
  const query = params.toString();
  const res = await fetch(
    apiUrl(`/php/get_task_reviews.php${query ? `?${query}` : ''}`)
  );
  const result = await parseTaskResponse<TaskListResponse<TaskReviewRow>>(
    res,
    'Не вдалося завантажити завдання на перевірку'
  );
  return result.data ?? [];
};

/** GET — tasks available to the session user, with their completion state. */
export const getUserTasks = async (): Promise<UserTaskRow[]> => {
  const res = await fetch(apiUrl('/php/get_user_tasks.php'));
  const result = await parseTaskResponse<TaskListResponse<UserTaskRow>>(
    res,
    'Не вдалося завантажити завдання'
  );
  return result.data ?? [];
};

/** POST JSON `{ completion_id, action }` — approve (credits coins) or reject. */
export const processTaskReview = async (
  args: ProcessTaskReviewArgs
): Promise<TaskActionResponse> => {
  const res = await fetch(apiUrl('/php/process_task_review.php'), {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(args),
  });
  return parseTaskResponse<TaskActionResponse>(
    res,
    'Помилка перевірки завдання'
  );
};

/**
 * POST JSON — create a task. The API doc says "Body / FormData", but the PHP
 * reads the JSON body and answers 400 «Порожній запит.» to multipart.
 */
export const createTask = async (
  args: CreateTaskArgs
): Promise<CreateTaskResponse> => {
  const res = await fetch(apiUrl('/php/process_add_task.php'), {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(args),
  });
  return parseTaskResponse<CreateTaskResponse>(
    res,
    'Не вдалося створити завдання'
  );
};

/**
 * POST (multipart) — employee's proof of completion: `comment` (text/link)
 * and/or a `screenshot` file. The PHP ignores `proof_text`/`proof_file`.
 */
export const submitTaskProof = async (
  args: SubmitTaskProofArgs
): Promise<TaskActionResponse> => {
  const res = await fetch(apiUrl('/php/submit_task_proof.php'), {
    method: 'POST',
    body: toFormData({ ...args }),
  });
  return parseTaskResponse<TaskActionResponse>(
    res,
    'Не вдалося відправити підтвердження'
  );
};
